import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, MessageCircle, X, Minimize2, Maximize2, Database } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useApiTokens } from '@/hooks/useApiTokens';
import { useMyTasks } from '@/hooks/useMyTasks';
import { useSopAreas } from '@/hooks/useSopAreas';
import { useSopTasks } from '@/hooks/useSopTasks';
import { useSopTaskGroups } from '@/hooks/useSopTaskGroups';
import { useAiAssistantData } from '@/hooks/useAiAssistantData';
import { useAiAssistant } from '@/hooks/useAiAssistant';
import { MessageDisplay } from './MessageDisplay';

interface AiAssistantChatProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const AiAssistantChat: React.FC<AiAssistantChatProps> = ({ isOpen, onToggle }) => {
  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [staticWelcomeMessage, setStaticWelcomeMessage] = useState<string | null>(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId, currentBrand } = useBrandManager();
  
  // Use the enhanced AI assistant hook
  const { 
    messages, 
    sendMessage: sendAiMessage, 
    clearMessages, 
    isLoading,
    isFunctionCalling,
    markTypingComplete
  } = useAiAssistant();

  const { apiTokens } = useApiTokens(currentBrandId);
  const { foundationalTasks, growthTasks, recurringTasks } = useMyTasks();
  const { data: sopAreas } = useSopAreas(currentBrandId || '');
  const { data: sopTasks } = useSopTasks(currentBrandId || '');
  const { data: sopTaskGroups } = useSopTaskGroups(currentBrandId || '');

  // Get personalized data for welcome message
  const { assistantData, isLoading: isAssistantDataLoading } = useAiAssistantData(
    currentBrandId,
    user,
    foundationalTasks?.length || 0,
    growthTasks?.length || 0,
    recurringTasks?.length || 0,
    sopTasks?.length || 0
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setUserHasScrolled(false);
  };

  const isScrolledToBottom = () => {
    if (!scrollAreaRef.current) return true;
    const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
    if (!scrollElement) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    return scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
  };

  const handleScroll = () => {
    if (!isScrolledToBottom()) {
      setUserHasScrolled(true);
    } else {
      setUserHasScrolled(false);
    }
  };

  // Only auto-scroll when user hasn't manually scrolled and it's not during typing
  useEffect(() => {
    const hasTypingMessage = messages.some(msg => msg.isTyping);
    
    // Auto-scroll for new user messages or when typing completes, but not during typing
    if (!userHasScrolled && !hasTypingMessage) {
      scrollToBottom();
    }
  }, [messages, userHasScrolled]);

  // Show static welcome message when the chat opens (without sending to AI)
  useEffect(() => {
    if (isOpen && !hasShownWelcome && !isAssistantDataLoading && assistantData && user) {
      const welcomeMessage = `Hello ${assistantData.firstName?.charAt(0).toUpperCase() + assistantData.firstName?.slice(1) || 'there'},

You can ask to "create a task", "create a brainstorming idea", "what's my open tasks" or "where can I see my competitors"`;

      setStaticWelcomeMessage(welcomeMessage);
      setHasShownWelcome(true);
    }
  }, [isOpen, hasShownWelcome, isAssistantDataLoading, assistantData, user]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading || !currentBrandId || !user) {
      return;
    }

    // Check if OpenAI API key is configured
    if (!apiTokens.openai || !apiTokens.openai_verified) {
      toast({
        title: "API Key Required",
        description: "Please configure your OpenAI API key in Settings first.",
        variant: "destructive",
      });
      return;
    }

    const messageContent = input.trim();
    setInput('');
    
    try {
      await sendAiMessage(messageContent, currentBrandId, user.id);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <Card className={`fixed bottom-4 right-4 w-96 shadow-lg border z-50 transition-all duration-200 flex flex-col ${
      isMinimized ? 'h-14' : 'h-[500px]'
    }`}>
      <CardHeader className="flex flex-row items-center justify-between p-3 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4" />
          <CardTitle className="text-sm font-medium">AI Assistant</CardTitle>
          {isFunctionCalling && (
            <Badge variant="secondary" className="text-xs animate-pulse">
              <Database className="h-3 w-3 mr-1" />
              Live Data
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary-foreground/20 relative z-10"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMinimized(!isMinimized);
            }}
          >
            {isMinimized ? <Maximize2 className="h-3 w-3" /> : <Minimize2 className="h-3 w-3" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary-foreground/20 relative z-10"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle();
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>

      {!isMinimized && (
        <CardContent className="p-0 flex flex-col flex-1 min-h-0">
          <ScrollArea 
            ref={scrollAreaRef}
            className="flex-1 p-3" 
            onScrollCapture={handleScroll}
          >
            <div className="space-y-4">
              {/* Static welcome message */}
              {staticWelcomeMessage && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg p-3 bg-primary text-primary-foreground">
                    <MessageDisplay 
                      content={staticWelcomeMessage}
                      isTyping={false}
                      messageId="static-welcome"
                      onTypingComplete={markTypingComplete}
                    />
                  </div>
                </div>
              )}
              
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg p-3 ${
                    message.isUser 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                  }`}>
                    <MessageDisplay 
                      content={message.content}
                      isTyping={message.isTyping}
                      messageId={message.id}
                      onTypingComplete={markTypingComplete}
                    />
                    {message.functionCalls && message.functionCalls.length > 0 && (
                      <div className="mt-2 text-xs opacity-70 flex items-center gap-1">
                        <Database className="h-3 w-3" />
                        Used live data: {message.functionCalls.map(fc => fc.function).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {(isLoading || isFunctionCalling) && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{isFunctionCalling ? 'Fetching your data...' : 'Thinking...'}</span>
                </div>
              )}
            </div>
            <div ref={messagesEndRef} />
          </ScrollArea>

          <div className="border-t p-3 flex-shrink-0">
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about your tasks, squad, or anything else..."
                className="flex-1 min-h-[40px] max-h-[100px] resize-none"
                disabled={isLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                size="sm"
                className="h-10 w-10 p-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};